/**
 * Facebook/Instagram API Integration
 * 
 * Handles Facebook Graph API for fetching posts from Facebook and Instagram.
 * Supports read-only operations with minimal API requirements.
 */

import { SocialMediaService, SocialPost, SocialAPIResponse, FeedParams, SocialPlatform, SocialMediaItem } from '../../types/social';

/**
 * Facebook Graph API response interfaces
 */
interface FacebookPost {
  id: string;
  message?: string;
  story?: string;
  full_picture?: string;
  picture?: string;
  created_time: string;
  updated_time?: string;
  permalink_url: string;
  type: string;
  status_type?: string;
  from: {
    id: string;
    name: string;
    picture?: {
      data: {
        url: string;
      };
    };
  };
  likes?: {
    summary: {
      total_count: number;
    };
  };
  comments?: {
    summary: {
      total_count: number;
    };
  };
  shares?: {
    count: number;
  };
  attachments?: {
    data: Array<{
      type: string;
      media?: {
        image?: {
          src: string;
          width: number;
          height: number;
        };
      };
      subattachments?: {
        data: Array<{
          media?: {
            image?: {
              src: string;
              width: number;
              height: number;
            };
          };
        }>;
      };
    }>;
  };
}

interface FacebookUser {
  id: string;
  name: string;
  username?: string;
  picture?: {
    data: {
      url: string;
    };
  };
  verified?: boolean;
}

/**
 * Facebook/Instagram API Service
 */
export class FacebookService implements SocialMediaService {
  platform: SocialPlatform = 'facebook';
  private accessToken: string;
  private pageId?: string;
  private instagramBusinessAccountId?: string;
  private baseURL = 'https://graph.facebook.com/v18.0';

  constructor(config: {
    accessToken: string;
    pageId?: string;
    instagramBusinessAccountId?: string;
  }) {
    this.accessToken = config.accessToken;
    this.pageId = config.pageId;
    this.instagramBusinessAccountId = config.instagramBusinessAccountId;
  }

  /**
   * Transform Facebook post to unified format
   */
  private transformPost(fbPost: FacebookPost, platform: SocialPlatform = 'facebook'): SocialPost {
    const content = fbPost.message || fbPost.story || '';
    const media = this.extractMedia(fbPost);
    const hashtags = this.extractHashtags(content);
    const mentions = this.extractMentions(content);

    return {
      id: fbPost.id,
      platform,
      content,
      author: {
        id: fbPost.from.id,
        username: fbPost.from.name.toLowerCase().replace(/\s+/g, ''),
        displayName: fbPost.from.name,
        avatar: fbPost.from.picture?.data.url,
        verified: false
      },
      media,
      metrics: {
        likes: fbPost.likes?.summary.total_count || 0,
        comments: fbPost.comments?.summary.total_count || 0,
        shares: fbPost.shares?.count || 0,
        views: 0
      },
      timestamps: {
        created: new Date(fbPost.created_time),
        updated: fbPost.updated_time ? new Date(fbPost.updated_time) : undefined
      },
      url: fbPost.permalink_url,
      type: this.mapPostType(fbPost.type, fbPost.status_type),
      visibility: 'public',
      platformData: {
        originalType: fbPost.type,
        statusType: fbPost.status_type
      },
      hashtags,
      mentions
    };
  }

  /**
   * Extract media from Facebook post
   */
  private extractMedia(fbPost: FacebookPost): SocialMediaItem[] {
    const media: SocialMediaItem[] = [];

    // Add main picture if available
    if (fbPost.full_picture || fbPost.picture) {
      media.push({
        id: `${fbPost.id}_main`,
        type: 'image',
        url: fbPost.full_picture || fbPost.picture!,
        dimensions: undefined
      });
    }

    // Add attachment media
    if (fbPost.attachments?.data) {
      fbPost.attachments.data.forEach((attachment, index) => {
        if (attachment.media?.image) {
          media.push({
            id: `${fbPost.id}_${index}`,
            type: 'image',
            url: attachment.media.image.src,
            dimensions: {
              width: attachment.media.image.width,
              height: attachment.media.image.height
            }
          });
        }

        // Handle subattachments (for carousel posts)
        if (attachment.subattachments?.data) {
          attachment.subattachments.data.forEach((sub, subIndex) => {
            if (sub.media?.image) {
              media.push({
                id: `${fbPost.id}_${index}_${subIndex}`,
                type: 'image',
                url: sub.media.image.src,
                dimensions: {
                  width: sub.media.image.width,
                  height: sub.media.image.height
                }
              });
            }
          });
        }
      });
    }

    return media;
  }

  /**
   * Map Facebook post type to unified type
   */
  private mapPostType(type: string, statusType?: string): SocialPost['type'] {
    if (type === 'photo' || statusType === 'added_photos') return 'image';
    if (type === 'video' || statusType === 'added_video') return 'video';
    if (type === 'link') return 'text';
    return 'text';
  }

  /**
   * Extract hashtags from content
   */
  private extractHashtags(content: string): string[] {
    const hashtags = content.match(/#[a-zA-Z0-9_]+/g);
    return hashtags ? hashtags.map(tag => tag.slice(1)) : [];
  }

  /**
   * Extract mentions from content
   */
  private extractMentions(content: string): string[] {
    const mentions = content.match(/@[a-zA-Z0-9_]+/g);
    return mentions ? mentions.map(mention => mention.slice(1)) : [];
  }

  /**
   * Fetch user posts from Facebook
   */
  async getUserPosts(params: FeedParams): Promise<SocialAPIResponse<SocialPost[]>> {
    try {
      let endpoint: string;
      let targetPlatform: SocialPlatform;

      if (params.platform === 'instagram' && this.instagramBusinessAccountId) {
        endpoint = `${this.baseURL}/${this.instagramBusinessAccountId}/media`;
        targetPlatform = 'instagram';
      } else {
        const pageId = params.userId || this.pageId;
        if (!pageId) {
          throw new Error('Page ID required for Facebook posts');
        }
        endpoint = `${this.baseURL}/${pageId}/posts`;
        targetPlatform = 'facebook';
      }

      const queryParams = new URLSearchParams({
        access_token: this.accessToken,
        limit: (params.limit || 25).toString(),
        fields: this.getFieldsForPlatform(targetPlatform)
      });

      if (params.nextToken) {
        queryParams.append('after', params.nextToken);
      }

      const response = await fetch(`${endpoint}?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Facebook API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(`Facebook API error: ${data.error.message}`);
      }

      const posts = data.data.map((post: FacebookPost) => 
        this.transformPost(post, targetPlatform)
      );

      return {
        data: posts,
        platform: targetPlatform,
        meta: {
          total: posts.length,
          nextToken: data.paging?.cursors?.after,
          rateLimit: {
            remaining: 200, // Facebook doesn't provide this in headers
            reset: new Date(Date.now() + 60 * 60 * 1000) // Estimate 1 hour
          }
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Facebook posts:', error);
      throw error;
    }
  }

  /**
   * Get fields parameter for different platforms
   */
  private getFieldsForPlatform(platform: SocialPlatform): string {
    const commonFields = [
      'id',
      'created_time',
      'updated_time',
      'permalink_url',
      'from{id,name,picture}',
      'likes.summary(true)',
      'comments.summary(true)',
      'shares'
    ];

    if (platform === 'instagram') {
      return [
        ...commonFields,
        'caption',
        'media_type',
        'media_url',
        'thumbnail_url',
        'timestamp'
      ].join(',');
    } else {
      return [
        ...commonFields,
        'message',
        'story',
        'full_picture',
        'picture',
        'type',
        'status_type',
        'attachments{media,subattachments}'
      ].join(',');
    }
  }

  /**
   * Fetch single post
   */
  async getPost(postId: string): Promise<SocialAPIResponse<SocialPost>> {
    try {
      const fields = this.getFieldsForPlatform('facebook');
      const response = await fetch(
        `${this.baseURL}/${postId}?access_token=${this.accessToken}&fields=${fields}`
      );

      if (!response.ok) {
        throw new Error(`Facebook API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(`Facebook API error: ${data.error.message}`);
      }

      const post = this.transformPost(data);

      return {
        data: post,
        platform: this.platform,
        meta: {
          rateLimit: {
            remaining: 200,
            reset: new Date(Date.now() + 60 * 60 * 1000)
          }
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Facebook post:', error);
      throw error;
    }
  }

  /**
   * Fetch user profile
   */
  async getUserProfile(username: string): Promise<SocialAPIResponse<SocialPost['author']>> {
    try {
      const response = await fetch(
        `${this.baseURL}/${username}?access_token=${this.accessToken}&fields=id,name,username,picture,verified`
      );

      if (!response.ok) {
        throw new Error(`Facebook API error: ${response.status} ${response.statusText}`);
      }

      const data: FacebookUser = await response.json();

      if (data.error) {
        throw new Error(`Facebook API error: ${data.error.message}`);
      }

      const profile = {
        id: data.id,
        username: data.username || data.name.toLowerCase().replace(/\s+/g, ''),
        displayName: data.name,
        avatar: data.picture?.data.url,
        verified: data.verified || false
      };

      return {
        data: profile,
        platform: this.platform,
        meta: {
          rateLimit: {
            remaining: 200,
            reset: new Date(Date.now() + 60 * 60 * 1000)
          }
        },
        timestamp: new Date()
      };

    } catch (error) {
      console.error('Failed to fetch Facebook profile:', error);
      throw error;
    }
  }

  /**
   * Validate credentials
   */
  async validateCredentials(): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseURL}/me?access_token=${this.accessToken}&fields=id,name`
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return !data.error;

    } catch (error) {
      console.error('Failed to validate Facebook credentials:', error);
      return false;
    }
  }

  /**
   * Get rate limit status
   */
  async getRateLimit(): Promise<{ remaining: number; reset: Date }> {
    // Facebook doesn't provide rate limit info in standard way
    // Return estimated values
    return {
      remaining: 200,
      reset: new Date(Date.now() + 60 * 60 * 1000)
    };
  }
}

/**
 * Mock Facebook service for demo purposes
 */
export class MockFacebookService implements SocialMediaService {
  platform: SocialPlatform = 'facebook';

  async getUserPosts(params: FeedParams): Promise<SocialAPIResponse<SocialPost[]>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const mockPosts: SocialPost[] = [
      {
        id: 'fb_1',
        platform: params.platform || 'facebook',
        content: 'Just launched our new product line! 🚀 Excited to share this journey with you all. #newproduct #startup #innovation',
        author: {
          id: 'user_123',
          username: 'techstartup',
          displayName: 'Tech Startup',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
          verified: true
        },
        media: [
          {
            id: 'img_1',
            type: 'image',
            url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600',
            dimensions: { width: 600, height: 400 }
          }
        ],
        metrics: {
          likes: 142,
          comments: 23,
          shares: 8,
          views: 1250
        },
        timestamps: {
          created: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        },
        url: 'https://facebook.com/techstartup/posts/1',
        type: 'image',
        visibility: 'public',
        hashtags: ['newproduct', 'startup', 'innovation'],
        mentions: []
      },
      {
        id: 'fb_2',
        platform: params.platform || 'facebook',
        content: 'Team meeting vibes! 💼 Working on some exciting features for our upcoming release. Stay tuned!',
        author: {
          id: 'user_123',
          username: 'techstartup',
          displayName: 'Tech Startup',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
          verified: true
        },
        media: [
          {
            id: 'img_2',
            type: 'image',
            url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600',
            dimensions: { width: 600, height: 400 }
          }
        ],
        metrics: {
          likes: 89,
          comments: 12,
          shares: 4,
          views: 890
        },
        timestamps: {
          created: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
        },
        url: 'https://facebook.com/techstartup/posts/2',
        type: 'image',
        visibility: 'public',
        hashtags: [],
        mentions: []
      },
      {
        id: 'fb_3',
        platform: params.platform || 'facebook',
        content: 'Coffee fueled coding session ☕ Building the future one line at a time. #coding #developer #coffeecode',
        author: {
          id: 'user_123',
          username: 'techstartup',
          displayName: 'Tech Startup',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
          verified: true
        },
        media: [
          {
            id: 'img_3',
            type: 'image',
            url: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600',
            dimensions: { width: 600, height: 400 }
          }
        ],
        metrics: {
          likes: 156,
          comments: 31,
          shares: 12,
          views: 2100
        },
        timestamps: {
          created: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
        },
        url: 'https://facebook.com/techstartup/posts/3',
        type: 'image',
        visibility: 'public',
        hashtags: ['coding', 'developer', 'coffeecode'],
        mentions: []
      }
    ];

    // Filter posts based on platform
    const platformPosts = mockPosts.map(post => ({
      ...post,
      platform: params.platform || 'facebook'
    }));

    return {
      data: platformPosts.slice(0, params.limit || 10),
      platform: params.platform || 'facebook',
      meta: {
        total: platformPosts.length,
        page: 1,
        perPage: params.limit || 10,
        rateLimit: {
          remaining: 150,
          reset: new Date(Date.now() + 60 * 60 * 1000)
        }
      },
      timestamp: new Date()
    };
  }

  async getPost(postId: string): Promise<SocialAPIResponse<SocialPost>> {
    const posts = await this.getUserPosts({ platform: 'facebook' });
    const post = posts.data.find(p => p.id === postId);
    
    if (!post) {
      throw new Error('Post not found');
    }

    return {
      data: post,
      platform: this.platform,
      meta: {
        rateLimit: {
          remaining: 149,
          reset: new Date(Date.now() + 60 * 60 * 1000)
        }
      },
      timestamp: new Date()
    };
  }

  async getUserProfile(username: string): Promise<SocialAPIResponse<SocialPost['author']>> {
    await new Promise(resolve => setTimeout(resolve, 500));

    return {
      data: {
        id: 'user_123',
        username: 'techstartup',
        displayName: 'Tech Startup',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
        verified: true
      },
      platform: this.platform,
      meta: {
        rateLimit: {
          remaining: 199,
          reset: new Date(Date.now() + 60 * 60 * 1000)
        }
      },
      timestamp: new Date()
    };
  }

  async validateCredentials(): Promise<boolean> {
    return true;
  }

  async getRateLimit(): Promise<{ remaining: number; reset: Date }> {
    return {
      remaining: 200,
      reset: new Date(Date.now() + 60 * 60 * 1000)
    };
  }
}